// ============================================================================
// PsyReportsFilters — ОТБОРЫ отчётов: методика, пациент, период.
//
// Отдельный компонент (SRP): панель отчётов остаётся оркестратором, а разметка
// фильтров — здесь. Мобильная вёрстка: поля в сетку (1 колонка на телефоне),
// без горизонтальных прокруток и модальных окон.
// ============================================================================

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { PsyPatientOption } from '@/lib/psyReports';

export interface PsyReportsFiltersProps {
  methodCode: string;
  methodOptions: Array<{ code: string; name: string }>;
  patientId: string;
  /** Имя пациента, если его нет в загруженном списке (выбран в рабочем месте). */
  patientName: string;
  patients: PsyPatientOption[];
  patientWarning: string;
  from: string;
  to: string;
  filtersActive: boolean;
  /** Подпись действующего отбора (пациент/период). */
  filterLabel: string;
  onChangeMethod: (value: string) => void;
  onChangePatient: (value: string) => void;
  onChangeFrom: (value: string) => void;
  onChangeTo: (value: string) => void;
  onReset: () => void;
}

export function PsyReportsFilters({
  methodCode, methodOptions, patientId, patientName, patients, patientWarning,
  from, to, filtersActive, filterLabel,
  onChangeMethod, onChangePatient, onChangeFrom, onChangeTo, onReset,
}: PsyReportsFiltersProps) {
  const periodInvalid = !!(from && to && from > to);

  return (
    <div className="px-3 py-2 border-b shrink-0 space-y-2">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-0.5">
          <span className="text-[10px] text-muted-foreground">Методика</span>
          <select
            value={methodCode}
            onChange={(e) => onChangeMethod(e.target.value)}
            className="h-8 w-full rounded-md border bg-input px-2 text-xs outline-none focus:border-primary"
          >
            <option value="">Все методики</option>
            {methodOptions.map((m) => (
              <option key={m.code} value={m.code}>{m.name}</option>
            ))}
          </select>
        </label>
        <label className="space-y-0.5">
          <span className="text-[10px] text-muted-foreground">Пациент</span>
          <select
            value={patientId}
            onChange={(e) => onChangePatient(e.target.value)}
            disabled={patients.length === 0}
            className="h-8 w-full rounded-md border bg-input px-2 text-xs outline-none focus:border-primary disabled:opacity-60"
          >
            <option value="">Все пациенты</option>
            {patientId && !patients.some((p) => p.id === patientId) && (
              <option value={patientId}>{patientName || 'Выбранный пациент'}</option>
            )}
            {patients.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <label className="space-y-0.5">
          <span className="text-[10px] text-muted-foreground">Период с</span>
          <Input type="date" value={from} onChange={(e) => onChangeFrom(e.target.value)} className="h-8 text-xs" />
        </label>
        <label className="space-y-0.5">
          <span className="text-[10px] text-muted-foreground">Период по</span>
          <Input type="date" value={to} onChange={(e) => onChangeTo(e.target.value)} className="h-8 text-xs" />
        </label>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-muted-foreground">
          {filtersActive ? `Отбор: ${filterLabel}` : 'Отбор не задан — показаны все записи.'}
        </span>
        {filtersActive && (
          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onReset}>
            Сбросить отбор
          </Button>
        )}
        {periodInvalid && (
          <span className="text-[11px] text-destructive">Дата «с» позже даты «по» — записей не будет.</span>
        )}
        {patientWarning && (
          <span className="text-[11px] text-amber-600 dark:text-amber-400">{patientWarning}</span>
        )}
      </div>
    </div>
  );
}
